import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { message, Spin, Tag, Modal, Input, Alert } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { ButtonItem } from "../../../../Components/Items";
import { WA } from "../theme";

// Conexión de WhatsApp por autoservicio (Embedded Signup de Meta), un número por usuario.
const ConectarWhatsapp = ({ puedeMarcarPrincipal }) => {
  const [cfgMeta, setCfgMeta] = useState(null); // appId, configId, graphVersion
  // phone_number_id / waba_id del diálogo. En ref (no estado) para leerlo
  // fresco dentro del callback de FB.login y evitar el stale closure.
  const signupRef = useRef(null);
  const [conexion, setConexion] = useState({ activo: false });
  const [lineas, setLineas] = useState([]);
  const [numeroPorUsuario, setNumeroPorUsuario] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [conectando, setConectando] = useState(false);
  const [modalDesc, setModalDesc] = useState(false); // modal de confirmación de desconexión
  const [confirmTexto, setConfirmTexto] = useState("");

  const cargarEstado = () =>
    axios.post("whatsapp/estadoConexion").then((r) => {
      if (r.data.success === 2) {
        setConexion(r.data.conexion);
        setLineas(r.data.lineas || []);
        setNumeroPorUsuario(!!r.data.numeroPorUsuario);
      }
    });

  const marcarPrincipal = (lineaId) =>
    axios.post("whatsapp/marcarLineaPrincipal", { lineaId }).then((r) => {
      if (r.data.success === 2) {
        message.success("Número principal actualizado.");
        cargarEstado();
      } else {
        message.error(r.data.message || "No se pudo marcar el número principal.");
      }
    });

  useEffect(() => {
    axios.post("whatsapp/datosEmbeddedSignup").then((r) => {
      if (r.data.success === 2) {
        setCfgMeta(r.data);
        window.fbAsyncInit = () =>
          window.FB.init({
            appId: r.data.appId,
            autoLogAppEvents: true,
            xfbml: true,
            version: r.data.graphVersion,
          });
        if (!document.getElementById("fb-sdk")) {
          const s = document.createElement("script");
          s.id = "fb-sdk";
          s.async = true;
          s.defer = true;
          s.crossOrigin = "anonymous";
          s.src = "https://connect.facebook.net/en_US/sdk.js";
          document.body.appendChild(s);
        }
      }
    });
    cargarEstado().finally(() => setCargando(false));
  }, []);

  //  Capturar phone_number_id / waba_id de Meta
  useEffect(() => {
    const handler = (event) => {
      if (!String(event.origin).endsWith("facebook.com")) return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === "WA_EMBEDDED_SIGNUP") {
          if (data.event === "FINISH" || data.event === "FINISH_ONLY_WABA") {
            signupRef.current = {
              phoneNumberId: data.data?.phone_number_id,
              wabaId: data.data?.waba_id,
            };
          }
        }
      } catch {}
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const conectar = () => {
    if (window.location.protocol !== "https:") {
      return message.error("La conexión con WhatsApp no está disponible en este momento.");
    }
    if (!window.FB) return message.error("El SDK de Facebook no se ha cargado aún.");
    if (conectando) return;

    setConectando(true);
    window.FB.login(
      (response) => {
        const code = response?.authResponse?.code;
        const sg = signupRef.current;
        if (!code || !sg?.phoneNumberId || !sg?.wabaId) {
          setConectando(false);
          return message.error("No se completó la conexión.");
        }
        axios
          .post("whatsapp/finalizarOnboarding", { code, ...sg })
          .then((r) => {
            if (r.data.success === 2) {
              message.success(`WhatsApp conectado: ${r.data.numero}`);
              return cargarEstado();
            }
            message.error(r.data.message || "No se pudo conectar WhatsApp.");
          })
          .catch(() => message.error("No se pudo conectar WhatsApp."))
          .finally(() => setConectando(false));
      },
      {
        config_id: cfgMeta.configId,
        response_type: "code",
        override_default_response_type: true,
        extras: { sessionInfoVersion: "2" },
      },
    );
  };

  const confirmacionValida = confirmTexto.trim().toLowerCase() === "desconectar";

  const desconectar = () => {
    if (!confirmacionValida) return;
    axios.post("whatsapp/desconectar").then((r) => {
      if (r.data.success === 2) {
        message.success(numeroPorUsuario ? "Tu número fue desconectado." : "El número de la empresa fue desconectado.");
        setModalDesc(false);
        setConfirmTexto("");
        cargarEstado();
      } else {
        message.error(r.data.message || "No se pudo desconectar.");
      }
    });
  };

  if (cargando) {
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        <Spin />
      </div>
    );
  }

  const listaLineas = lineas.length ? (
    <div style={{ marginTop: 24 }}>
      <div style={{ color: WA.textPrimary, fontWeight: 600, marginBottom: 8 }}>
        Números conectados en la empresa
      </div>
      {numeroPorUsuario && (
        <div style={{ color: WA.textSecondary, fontSize: 12, marginBottom: 8 }}>
          El número principal es el buzón de entrada: los chats que lleguen ahí se pueden pasar a
          un vendedor.
        </div>
      )}
      {lineas.map((l) => (
        <div
          key={l._id}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            border: `1px solid ${l.principal ? WA.limaFuerte : WA.border}`,
            borderRadius: 8,
            marginBottom: 6,
          }}
        >
          <span style={{ color: WA.textPrimary }}>
            <b>{l.numeroDisplay}</b>
            {l.nombreVerificado ? ` — ${l.nombreVerificado}` : ""}
            {l.principal && (
              <Tag style={{ marginLeft: 8, backgroundColor: WA.lima, color: "#1f1f1f", border: "none" }}>
                Principal
              </Tag>
            )}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: WA.textSecondary }}>
              {l.usuario || "Sin usuario"}
              {l.propia ? " (tú)" : ""}
            </span>
            {numeroPorUsuario && puedeMarcarPrincipal && !l.principal && (
              <ButtonItem turquesa lessTime onClick={() => marcarPrincipal(l._id)}>
                Hacer principal
              </ButtonItem>
            )}
          </span>
        </div>
      ))}
    </div>
  ) : null;

  if (conexion.activo) {
    return (
      <div style={{ padding: 16 }}>
        <Tag
          icon={<CheckCircleOutlined />}
          style={{
            fontSize: 14,
            padding: "4px 10px",
            backgroundColor: WA.lima,
            color: "#1f1f1f",
            border: "none",
          }}
        >
          Conectado
        </Tag>
        <div style={{ marginTop: 10, color: WA.textPrimary }}>
          {numeroPorUsuario ? "Tu número" : "Número de la empresa"}: <b>{conexion.numeroDisplay}</b>
          {conexion.nombreVerificado ? ` — ${conexion.nombreVerificado}` : ""}
        </div>
        <ButtonItem rojo style={{ marginTop: 16 }} onClick={() => setModalDesc(true)}>
          {numeroPorUsuario ? "Desconectar mi número" : "Desconectar número de la empresa"}
        </ButtonItem>

        {numeroPorUsuario && listaLineas}

        <Modal
          title={numeroPorUsuario ? "Desconectar mi número" : "Desconectar número de la empresa"}
          visible={modalDesc}
          onCancel={() => {
            setModalDesc(false);
            setConfirmTexto("");
          }}
          onOk={desconectar}
          okText="Desconectar"
          cancelText="Cancelar"
          okButtonProps={{ danger: true, disabled: !confirmacionValida }}
        >
          {numeroPorUsuario ? (
            <p>
              Esta acción desconectará <b>únicamente tu número</b> y dejarás de enviar y recibir
              mensajes desde él. Los números de tus compañeros no se ven afectados. Para confirmar,
              escribe <b>desconectar</b> abajo.
            </p>
          ) : (
            <p>
              Esta acción desconectará <b>el número de la empresa</b> y nadie podrá enviar ni
              recibir mensajes por WhatsApp hasta que se conecte de nuevo. Para confirmar, escribe{" "}
              <b>desconectar</b> abajo.
            </p>
          )}
          <Input
            placeholder="desconectar"
            value={confirmTexto}
            onChange={(e) => setConfirmTexto(e.target.value)}
            onPressEnter={desconectar}
          />
        </Modal>
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <p style={{ color: WA.textSecondary }}>
        {numeroPorUsuario
          ? "Conecta tu número de WhatsApp Business para enviar y recibir mensajes desde el CRM con tu propia identidad."
          : "Conecta el número de WhatsApp Business de la empresa; todos los usuarios enviarán y recibirán mensajes desde él."}
      </p>

      {numeroPorUsuario && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Selecciona la cuenta de WhatsApp Business que ya usa tu empresa"
          description={
            <>
              En el diálogo de Meta, elige la cuenta de WhatsApp Business <b>que ya existe</b> y
              agrégale tu número. <b>No crees una cuenta nueva</b>: si lo haces, tu número no
              compartirá las plantillas ni la configuración con el resto del equipo.
            </>
          }
        />
      )}

      <ButtonItem lima disabled={!cfgMeta || conectando} loading={conectando} onClick={conectar}>
        {numeroPorUsuario ? "Conectar mi número" : "Conectar número de la empresa"}
      </ButtonItem>

      {numeroPorUsuario && listaLineas}
    </div>
  );
};

export default ConectarWhatsapp;
